import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box as BoxType, BRAND_COLORS, FONTS, MeasurementUnit } from '../types/index.ts';
import { addBox, deleteBox, getAllBoxes, updateBox, toggleBoxVisibility, reconcileExampleBoxes } from '../services/database.ts';
import { getPreferredUnit, setPreferredUnit, getMarginInches, setMarginInches } from '../services/preferences.ts';
import { v4 as uuidv4 } from 'uuid';
import CsvImportExport from './CsvImportExport.tsx';

interface BoxManagementProps {
  onBoxesChanged?: () => void;
  // Signale un changement de préférence (unité / marge) pour que l'app
  // (status rail, invalidation des résultats) se rafraîchisse immédiatement.
  onPreferencesChanged?: () => void;
}

// Format a margin stored in inches for display/entry in the given unit.
const marginToDisplay = (inches: number, unit: MeasurementUnit): string => {
  const value = unit === 'cm' ? inches * 2.54 : inches;
  return String(parseFloat(value.toFixed(3)));
};

const BoxManagement: React.FC<BoxManagementProps> = ({ onBoxesChanged, onPreferencesChanged }) => {
  const [boxes, setBoxes] = useState<BoxType[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentBox, setCurrentBox] = useState<BoxType | null>(null);
  const [formValues, setFormValues] = useState({
    name: '',
    length: '',
    width: '',
    height: '',
    unit: getPreferredUnit()
  });
  const [errors, setErrors] = useState({
    name: false,
    dimensions: false,
  });
  // Display unit defaults to the user's remembered preference (in / cm).
  const [displayUnit, setDisplayUnit] = useState<MeasurementUnit>(getPreferredUnit());
  // Safety margin input, shown in the current display unit (stored in inches).
  const [marginInput, setMarginInput] = useState<string>(() =>
    marginToDisplay(getMarginInches(), getPreferredUnit())
  );
  // Caisse supprimée en attente d'annulation (snackbar UNDO).
  const [deletedBox, setDeletedBox] = useState<BoxType | null>(null);

  // Une marge invalide (négative, non numérique) est affichée en erreur au lieu
  // d'être ignorée en silence pendant que l'ancienne valeur reste appliquée.
  const marginInvalid =
    marginInput.trim() !== '' && !(Number(marginInput.replace(',', '.')) >= 0);

  useEffect(() => {
    // Load boxes when component starts (run once on mount).
    loadBoxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBoxes = () => {
    // Réconcilie le mode démo AVANT de lire, pour que le tableau reflète
    // immédiatement la suppression des exemples après un ajout / import (et le
    // re-seed si la base vient d'être vidée).
    reconcileExampleBoxes();
    const loadedBoxes = getAllBoxes();
    setBoxes(loadedBoxes);
    if (onBoxesChanged) {
      onBoxesChanged();
    }
  };

  const handleOpenDialog = (box?: BoxType) => {
    if (box) {
      // Edit mode
      setCurrentBox(box);
      
      // Convert dimensions if needed
      const boxUnit = box.unit || 'in';
      let dimensions = { ...box.dimensions };
      
      setFormValues({
        name: box.name,
        length: dimensions.length.toString(),
        width: dimensions.width.toString(),
        height: dimensions.height.toString(),
        unit: boxUnit
      });
    } else {
      // Add mode — default to the user's remembered unit.
      setCurrentBox(null);
      setFormValues({
        name: '',
        length: '',
        width: '',
        height: '',
        unit: getPreferredUnit()
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setErrors({ name: false, dimensions: false });
  };

  const validateForm = () => {
    // parseFloat('') is NaN, and NaN <= 0 is false, so require > 0 explicitly:
    // this rejects empty / NaN / non-positive dimensions.
    const length = parseFloat(formValues.length);
    const width = parseFloat(formValues.width);
    const height = parseFloat(formValues.height);
    const newErrors = {
      name: !formValues.name.trim(),
      dimensions: !(length > 0) || !(width > 0) || !(height > 0),
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.dimensions;
  };

  // Convert dimensions from one unit to another (factors are exact inverses)
  const convertDimensions = (dimensions: { length: number; width: number; height: number }, fromUnit: MeasurementUnit, toUnit: MeasurementUnit) => {
    if (fromUnit === toUnit) return dimensions;

    const factor = fromUnit === 'cm' && toUnit === 'in' ? 1 / 2.54 : 2.54;

    return {
      length: parseFloat((dimensions.length * factor).toFixed(3)),
      width: parseFloat((dimensions.width * factor).toFixed(3)),
      height: parseFloat((dimensions.height * factor).toFixed(3))
    };
  };

  const handleSaveBox = () => {
    if (!validateForm()) {
      return;
    }

    const boxData: BoxType = {
      id: currentBox ? currentBox.id : uuidv4(),
      name: formValues.name,
      dimensions: {
        length: parseFloat(formValues.length),
        width: parseFloat(formValues.width),
        height: parseFloat(formValues.height),
      },
      unit: formValues.unit
    };

    if (currentBox) {
      // Update existing box
      updateBox(boxData);
    } else {
      // Add new box
      addBox(boxData);
    }

    loadBoxes();
    handleCloseDialog();
  };

  // Suppression immédiate + annulation possible (snackbar) : plus fluide et
  // plus sûr qu'un window.confirm bloquant sans retour en arrière.
  const handleDeleteBox = (boxId: string) => {
    const box = boxes.find((b) => b.id === boxId) ?? null;
    deleteBox(boxId);
    loadBoxes();
    setDeletedBox(box);
  };

  const handleUndoDelete = () => {
    if (deletedBox) {
      addBox(deletedBox);
      loadBoxes();
    }
    setDeletedBox(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof formValues
  ) => {
    let value = e.target.value;
    
    // For dimension fields, accept comma as decimal separator
    if (['length', 'width', 'height'].includes(field)) {
      // Replace commas with dots for internal storage
      value = value.replace(',', '.');
    }
    
    setFormValues({
      ...formValues,
      [field]: value,
    });
  };

  const handleUnitChange = (unit: MeasurementUnit) => {
    if (unit === formValues.unit) return; // No change needed

    // Remember the choice so it persists across navigation / reloads.
    setPreferredUnit(unit);
    onPreferencesChanged?.();

    // Convert the dimensions to the new unit. Les champs vides ou non
    // numériques restent tels quels (pas de « 0 » à effacer à la main).
    const factor = formValues.unit === 'cm' && unit === 'in' ? 1 / 2.54 : 2.54;
    const convertField = (raw: string): string => {
      if (raw.trim() === '') return raw;
      const value = parseFloat(raw);
      if (!Number.isFinite(value)) return raw;
      return String(parseFloat((value * factor).toFixed(3)));
    };

    setFormValues({
      ...formValues,
      length: convertField(formValues.length),
      width: convertField(formValues.width),
      height: convertField(formValues.height),
      unit
    });
  };
  
  const handleDisplayUnitChange = (unit: MeasurementUnit) => {
    // Remember the choice so it persists across navigation / reloads.
    setPreferredUnit(unit);
    setDisplayUnit(unit);
    // Re-express the margin in the newly selected unit (same physical value).
    setMarginInput(marginToDisplay(getMarginInches(), unit));
    onPreferencesChanged?.();
  };

  const handleMarginChange = (raw: string) => {
    // Accept comma as decimal separator, like the dimension fields.
    const cleaned = raw.replace(',', '.');
    setMarginInput(cleaned);

    if (cleaned.trim() === '') {
      setMarginInches(0);
      onPreferencesChanged?.();
      return;
    }
    const parsed = parseFloat(cleaned);
    if (Number.isFinite(parsed) && parsed >= 0) {
      // Store canonically in inches.
      setMarginInches(displayUnit === 'cm' ? parsed / 2.54 : parsed);
      onPreferencesChanged?.();
    }
  };
  
  // Display dimensions in the selected unit
  const getDisplayDimensions = (box: BoxType) => {
    const boxUnit = box.unit || 'in';
    if (boxUnit === displayUnit) {
      return box.dimensions;
    }
    return convertDimensions(box.dimensions, boxUnit, displayUnit);
  };

  return (
    <Box sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" gutterBottom sx={{ color: BRAND_COLORS.primary, fontWeight: 'bold' }}>
        Box Management
      </Typography>

      <Typography variant="body1" paragraph>
        Manage your box inventory. Add, edit or remove boxes to use in packaging calculations.
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: BRAND_COLORS.secondary,
            '&:hover': {
              backgroundColor: '#0C7038',
            },
          }}
        >
          Add New Box
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Display unit:
          </Typography>
          <ToggleButtonGroup
            value={displayUnit}
            exclusive
            onChange={(_, unit) => unit && handleDisplayUnitChange(unit as MeasurementUnit)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { fontFamily: FONTS.mono } }}
          >
            <ToggleButton value="in" sx={{ px: 2 }}>in</ToggleButton>
            <ToggleButton value="cm" sx={{ px: 2 }}>cm</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Safety margin — clearance kept on every side of the box */}
        <Tooltip describeChild title="Margin kept around the product so it can be placed in the box.">
          <TextField
            label="Safety margin"
            type="number"
            size="small"
            value={marginInput}
            onChange={(e) => handleMarginChange(e.target.value)}
            error={marginInvalid}
            helperText={marginInvalid ? 'Must be 0 or more' : ''}
            InputProps={{
              endAdornment: <InputAdornment position="end">{displayUnit}</InputAdornment>,
              inputProps: { min: 0, step: 0.1 },
            }}
            sx={{ width: 180, '& input': { fontFamily: FONTS.mono } }}
          />
        </Tooltip>

        <CsvImportExport onDataImported={loadBoxes} />
      </Box>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', borderTop: `3px solid ${BRAND_COLORS.kraft}` }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: BRAND_COLORS.primary }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Length ({displayUnit})</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Width ({displayUnit})</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Height ({displayUnit})</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boxes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No boxes found. Start by adding a new box.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                boxes.map((box) => {
                  const displayDimensions = getDisplayDimensions(box);
                  
                  return (
                    <TableRow key={box.id} hover>
                      <TableCell>{box.name}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: FONTS.mono }}>{displayDimensions.length.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: FONTS.mono }}>{displayDimensions.width.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: FONTS.mono }}>{displayDimensions.height.toFixed(2)}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit Box">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenDialog(box)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={box.visible === false ? "Show in results" : "Hide from results"}>
                          <IconButton
                            color={box.visible === false ? "default" : "primary"}
                            onClick={() => {
                              toggleBoxVisibility(box.id);
                              loadBoxes(); // Reload boxes to reflect the change
                            }}
                            size="small"
                            sx={{ mx: 1 }}
                          >
                            {box.visible === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Box">
                          <IconButton
                            sx={{ color: BRAND_COLORS.red }}
                            onClick={() => handleDeleteBox(box.id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Annulation de suppression : la caisse revient telle quelle. */}
      <Snackbar
        open={deletedBox !== null}
        autoHideDuration={6000}
        onClose={() => setDeletedBox(null)}
        message={deletedBox ? `"${deletedBox.name}" deleted` : ''}
        action={
          <Button color="secondary" size="small" onClick={handleUndoDelete}>
            UNDO
          </Button>
        }
      />

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: BRAND_COLORS.primary, color: 'white' }}>
          {currentBox ? 'Edit Box' : 'Add New Box'}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Box Name"
                fullWidth
                value={formValues.name}
                onChange={(e) => handleInputChange(e, 'name')}
                error={errors.name}
                helperText={errors.name ? 'Name is required' : ''}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>
                Unit
              </Typography>
              <ToggleButtonGroup
                value={formValues.unit}
                exclusive
                onChange={(_, unit) => unit && handleUnitChange(unit as MeasurementUnit)}
                size="small"
                sx={{ mb: 2, '& .MuiToggleButton-root': { fontFamily: FONTS.mono } }}
              >
                <ToggleButton value="in" sx={{ px: 3 }}>in</ToggleButton>
                <ToggleButton value="cm" sx={{ px: 3 }}>cm</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            
            <Grid item xs={12} sm={6}></Grid>
            
            <Grid item xs={12}>
              <Typography variant="overline" sx={{ display: 'block', mt: 2, mb: 1, fontFamily: FONTS.display, fontWeight: 700, letterSpacing: '0.08em', color: BRAND_COLORS.slateText }}>
                Internal Space
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter the internal dimensions of the box
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Length"
                type="number"
                fullWidth
                value={formValues.length}
                onChange={(e) => handleInputChange(e, 'length')}
                error={errors.dimensions}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{formValues.unit}</InputAdornment>,
                  inputProps: { step: '0.1' },
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Width"
                type="number"
                fullWidth
                value={formValues.width}
                onChange={(e) => handleInputChange(e, 'width')}
                error={errors.dimensions}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{formValues.unit}</InputAdornment>,
                  inputProps: { step: '0.1' },
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Height"
                type="number"
                fullWidth
                value={formValues.height}
                onChange={(e) => handleInputChange(e, 'height')}
                error={errors.dimensions}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{formValues.unit}</InputAdornment>,
                  inputProps: { step: '0.1' },
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
              />
            </Grid>
            {errors.dimensions && (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: BRAND_COLORS.red }}>
                  All dimensions must be greater than zero
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveBox}
            variant="contained"
            sx={{
              backgroundColor: BRAND_COLORS.secondary,
              '&:hover': {
                backgroundColor: '#0C7038',
              },
            }}
          >
            {currentBox ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoxManagement; 